<!--
  The plans, the billing toggle and the top-ups, rendered in two places.

  The public pricing page shows them to people deciding whether to sign up; the dashboard
  plan page shows them to people who already have an account, next to what they are using.
  Both read the same catalogue from the stripe service, so a price can never differ between
  the two, and this component owns the markup so the two cannot drift apart either.

  It decides nothing: the parent says which plans to draw, which tier is current, and what
  the call to action does.
-->
<template>
  <div class="pxc">
    <div class="px-toggle">
      <span :class="{ on: !yearly }">Monthly</span>
      <button
        type="button"
        role="switch"
        :aria-checked="yearly ? 'true' : 'false'"
        :class="{ y: yearly }"
        @click="$emit('update:yearly', !yearly)"
      >
        <i></i>
      </button>
      <span :class="{ on: yearly }">Yearly</span>
      <b class="px-save">2 months free</b>
    </div>

    <!-- plans -->
    <section class="px-plans">
      <div
        v-for="p in cards"
        :key="p.key"
        class="px-plan"
        :class="{ pop: p.popular, current: isCurrent(p), picked: isPicked(p) }"
      >
        <span v-if="isCurrent(p)" class="px-pop current">Your plan</span>
        <span v-else-if="isPicked(p)" class="px-pop picked"
          >The plan you chose</span
        >
        <span v-else-if="p.popular" class="px-pop">Most popular</span>
        <div class="px-plan-head">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <svg
            class="px-ic"
            viewBox="0 0 24 24"
            aria-hidden="true"
            v-html="icons[p.icon]"
          ></svg>
          <div>
            <h2>{{ p.name }}</h2>
            <p v-if="p.audience" class="px-aud">{{ p.audience }}</p>
          </div>
        </div>
        <p class="px-price">
          {{ priceOf(p) }}<span>{{ periodOf(p) }}</span>
        </p>
        <p class="px-blurb">{{ p.blurb }}</p>
        <p v-if="p.layer" class="px-layer">{{ p.layer }}</p>
        <ul>
          <li v-for="f in p.features" :key="labelOf(f)">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 12.5l4.5 4.5L19 7.5" />
            </svg>
            {{ labelOf(f) }}
            <em v-if="f.beta">BETA</em>
          </li>
        </ul>
        <router-link
          v-if="p.monthly === 0"
          class="px-cta"
          :class="{ quiet: isCurrent(p) }"
          :to="signedIn ? '/dashboard' : signupPath"
        >
          {{ signedIn ? freeLabel : p.cta }}
        </router-link>
        <button
          v-else
          class="px-cta"
          :class="{ gold: p.popular }"
          type="button"
          :disabled="!!busy"
          @click="$emit('subscribe', p)"
        >
          {{ ctaFor(p) }}
        </button>

        <div v-if="p.popular" class="px-pwp">
          <p class="px-pwp-t">Protected Work Programme</p>
          <p>
            Free credits for people whose work needs protection. Journalists,
            field reporters and researchers can apply by proving their
            profession with a verifiable credential.
          </p>
          <a :href="`mailto:${contact}?subject=Protected%20Work%20Programme`"
            >Apply here →</a
          >
        </div>
      </div>
    </section>

    <!-- top-ups -->
    <section v-if="topups.length" class="px-topups">
      <p class="px-k">Add-on top-ups</p>
      <div class="px-topup-row">
        <div v-for="t in topups" :key="t.label" class="px-topup">
          <span>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <svg
              viewBox="0 0 24 24"
              aria-hidden="true"
              v-html="icons[t.icon]"
            ></svg>
            {{ t.label }}</span
          ><b>{{ t.price }}</b>
        </div>
      </div>
      <p class="px-note">
        Beyond 100 interactive participants, Decast supports larger audiences in
        view-only mode.
      </p>
    </section>

    <!-- a tier with nothing to buy: quoted, not subscribed to -->
    <section v-if="band" class="px-band">
      <div class="px-band-head">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <svg
          class="px-ic"
          viewBox="0 0 24 24"
          aria-hidden="true"
          v-html="icons[band.icon]"
        ></svg>
        <div>
          <h2>{{ band.name }}</h2>
          <p class="px-aud">{{ band.audience }}</p>
        </div>
      </div>
      <div class="px-band-body">
        <h3>{{ band.blurb }}</h3>
        <p class="px-layer">{{ band.layer }}</p>
        <p v-if="band.footnote" class="px-band-foot">{{ band.footnote }}</p>
      </div>
      <a
        class="px-cta gold px-band-cta"
        :href="`mailto:${contact}?subject=${encodeURIComponent(band.name + ' — custom quote')}`"
      >
        {{ band.cta }} →
      </a>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PlanCards',
  props: {
    plans: { type: Array, required: true },
    topups: { type: Array, default: () => [] },
    icons: { type: Object, required: true },
    yearly: { type: Boolean, default: false },
    yearlyMonths: { type: Number, default: 10 },
    signedIn: { type: Boolean, default: false },
    currentTier: { type: String, default: '' },
    // the plan someone arrived wanting, so they can see which one they picked
    highlight: { type: String, default: '' },
    busy: { type: String, default: '' },
    signupPath: { type: String, default: '/register' },
    contact: { type: String, default: '' },
    freeLabel: { type: String, default: 'Included in your account' },
  },
  computed: {
    /** The tiers drawn as cards in the grid. */
    cards() {
      return this.plans.filter((p) => p.layout !== 'band');
    },
    /** The one drawn full width underneath, if the catalogue carries one. */
    band() {
      return this.plans.find((p) => p.layout === 'band') || null;
    },
  },
  methods: {
    labelOf(f) {
      return typeof f === 'string' ? f : f.label;
    },
    isCurrent(p) {
      return !!this.currentTier && p.key === this.currentTier;
    },
    isPicked(p) {
      return !!this.highlight && p.key === this.highlight && !this.isCurrent(p);
    },
    priceOf(p) {
      if (p.priceLabel) return p.priceLabel;
      if (p.monthly === 0) return '\u20ac0';
      return `\u20ac${this.yearly ? p.monthly * this.yearlyMonths : p.monthly}`;
    },
    periodOf(p) {
      if (p.priceLabel) return '';
      if (p.monthly === 0) return ' forever';
      return this.yearly ? ' / year' : ' / month';
    },
    ctaFor(p) {
      if (this.busy === p.key) return 'Opening checkout\u2026';
      if (this.isCurrent(p)) return 'Current plan \u00b7 Manage';
      if (this.currentTier && this.currentTier !== 'secure')
        return 'Change plan';
      return p.cta;
    },
  },
};
</script>

<style lang="scss" scoped>
.pxc {
  color: #fff;

  * {
    box-sizing: border-box;
    margin: 0;
  }
  h2,
  h3 {
    color: #fff;
    letter-spacing: -0.02em;
  }
  ul {
    list-style: none;
    padding: 0;
  }
  a {
    text-decoration: none;
    color: inherit;
  }
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  }
  .gold {
    color: #d7df23;
  }
  .px-toggle {
    margin-top: 30px;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #ffffff1f;
    background: #ffffff0a;
    border-radius: 999px;
    padding: 9px 18px;
    font-size: 13.5px;
    span {
      color: #ffffff8f;
    }
    span.on {
      color: #fff;
      font-weight: 600;
    }
    button {
      position: relative;
      width: 44px;
      height: 24px;
      border-radius: 999px;
      background: #ffffff33;
      transition: background 0.2s;
    }
    button.y {
      background: #d7df23;
    }
    button i {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #fff;
      transition: transform 0.2s;
    }
    button.y i {
      transform: translateX(20px);
    }
    .px-save {
      background: #d7df2326;
      color: #d7df23;
      border-radius: 999px;
      padding: 2px 9px;
      font-size: 11.5px;
      font-weight: 600;
    }
  }
  .px-plans {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
    align-items: start;
    padding-top: 14px;
  }
  .px-plan {
    position: relative;
    border: 1px solid #ffffff26;
    border-radius: 16px;
    background: #ffffff08;
    padding: 26px 24px;
    display: flex;
    flex-direction: column;
    &.pop {
      border-color: #d7df2399;
      background: #ffffff0f;
    }
  }
  .px-pop {
    position: absolute;
    top: -12px;
    left: 22px;
    background: #d7df23;
    color: #0b0c14;
    border-radius: 999px;
    padding: 2px 12px;
    font-size: 12px;
    font-weight: 700;
  }
  .px-plan-head {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .px-ic {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    padding: 8px;
    border: 1px solid #ffffff1a;
    background: #ffffff0d;
    border-radius: 10px;
    color: #d7df23;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .px-plan-head h2 {
    font-size: 18px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fff;
    font-weight: 700;
  }
  .px-layer {
    color: #d7df23;
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-top: 8px;
    line-height: 1.5;
  }
  // who the tier is for, beside the name
  .px-aud {
    color: #d7df23;
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-top: 4px;
  }

  // the quoted tier: one wide band under the cards, not a fourth column
  .px-band {
    margin-top: 28px;
    padding: 26px 28px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.03);
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas: 'head cta' 'body cta';
    gap: 14px 24px;
    align-items: center;

    .px-band-head {
      grid-area: head;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .px-band-body {
      grid-area: body;
    }
    .px-band-cta {
      grid-area: cta;
      align-self: center;
      width: auto;
      padding: 0 22px;
      white-space: nowrap;
    }
    h2 {
      font-size: 17px;
    }
    h3 {
      font-size: 19px;
      font-weight: 600;
    }
    .px-band-foot {
      color: rgba(255, 255, 255, 0.62);
      font-size: 13px;
      line-height: 1.6;
      margin-top: 10px;
    }

    @media (max-width: 760px) {
      grid-template-columns: 1fr;
      grid-template-areas: 'head' 'body' 'cta';
      align-items: start;

      .px-band-cta {
        width: 100%;
        text-align: center;
      }
    }
  }
  .px-price {
    margin-top: 18px;
    font-size: 38px;
    font-weight: 800;
    span {
      font-size: 14px;
      font-weight: 400;
      color: #ffffff8f;
    }
  }
  .px-blurb {
    margin-top: 8px;
    color: #ffffffb3;
    font-size: 13.5px;
  }
  .px-plan ul {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .px-plan li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13.5px;
    color: #ffffffd9;
  }
  .px-plan li svg,
  .px-promises svg {
    width: 15px;
    height: 15px;
    flex: 0 0 15px;
    margin-top: 2px;
    stroke: #d7df23;
    fill: none;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .px-plan li em {
    font-style: normal;
    border: 1px solid #ffffff40;
    border-radius: 4px;
    padding: 0 5px;
    font-size: 9.5px;
    letter-spacing: 0.1em;
    color: #ffffff99;
    margin-left: 6px;
  }
  .px-cta {
    margin-top: 22px;
    display: block;
    text-align: center;
    border: 1px solid #ffffff4d;
    border-radius: 12px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    transition: opacity 0.15s;
    width: 100%;
    &:hover {
      opacity: 0.85;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
    &.gold {
      background: #d7df23;
      border-color: #d7df23;
      color: #0b0c14;
    }
  }
  .px-pwp {
    margin-top: 20px;
    border: 1px solid #d7df234d;
    background: #d7df230d;
    border-radius: 12px;
    padding: 15px;
    .px-pwp-t {
      color: #d7df23;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    p {
      margin-top: 8px;
      font-size: 12.5px;
      color: #ffffffbf;
      line-height: 1.55;
    }
    a {
      display: inline-block;
      margin-top: 8px;
      color: #d7df23;
      font-size: 12.5px;
      font-weight: 600;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .px-topups {
    padding-top: 36px;
  }
  .px-k {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #ffffff80;
    margin-bottom: 12px;
  }
  .px-topup-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  .px-topup {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid #ffffff1a;
    background: #ffffff08;
    border-radius: 12px;
    padding: 13px 16px;
    font-size: 13.5px;
    span {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      color: #ffffffd9;
    }
    span svg {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      color: #d7df23;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.7;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    b {
      white-space: nowrap;
    }
  }
  .px-note {
    margin-top: 14px;
    border: 1px solid #ffffff1a;
    background: #ffffff05;
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 13.5px;
    color: #ffffff99;
  }

  .px-plan.current {
    border-color: #d7df2366;
  }
  .px-plan.picked {
    border-color: #d7df23;
    box-shadow: 0 0 0 1px #d7df2340;
  }
  .px-pop.picked {
    background: #d7df23;
    color: #0a0b0f;
  }
  .px-pop.current {
    background: #d7df23;
    color: #0a0b0f;
  }
  .px-cta.quiet {
    opacity: 0.75;
  }

  @media (max-width: 900px) {
    .px-plans,
    .px-topup-row {
      grid-template-columns: 1fr;
    }
  }
}
</style>
